import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import optimizeImage from "../utils/optimizeImage";

export default function PlayersManagement() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [form, setForm] = useState({ name: "", jersey_number: "", goal_amount: 500, parent_email: "" });
  const [formPhoto, setFormPhoto] = useState(null);
  const [formPhotoPreview, setFormPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  const fetchPlayers = useCallback(async () => {
    const { data } = await supabase
      .from("players")
      .select("*")
      .order("jersey_number");
    if (data) setPlayers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const openAdd = () => {
    setEditingPlayer(null);
    setForm({ name: "", jersey_number: "", goal_amount: 500, parent_email: "" });
    setFormPhoto(null);
    setFormPhotoPreview(null);
    setShowForm(true);
  };

  const openEdit = (player) => {
    setEditingPlayer(player);
    setForm({
      name: player.name,
      jersey_number: player.jersey_number,
      goal_amount: player.goal_amount,
      parent_email: player.parent_email || "",
    });
    setFormPhoto(null);
    setFormPhotoPreview(player.photo_url || null);
    setShowForm(true);
  };

  const uploadPhoto = async (playerId, file) => {
    try {
      const optimized = await optimizeImage(file);
      const filePath = `${playerId}.webp`;

      const { error: uploadError } = await supabase.storage
        .from("player-photos")
        .upload(filePath, optimized, { upsert: true, contentType: "image/webp" });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from("player-photos")
        .getPublicUrl(filePath);

      // Cache-bust so the new photo shows immediately
      return `${urlData.publicUrl}?v=${Date.now()}`;
    } catch (err) {
      console.error("Upload error:", err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    if (editingPlayer) {
      const updates = {
        name: form.name,
        jersey_number: Number(form.jersey_number),
        goal_amount: Number(form.goal_amount),
        parent_email: form.parent_email || null,
      };

      if (formPhoto) {
        const photoUrl = await uploadPhoto(editingPlayer.id, formPhoto);
        if (photoUrl) updates.photo_url = photoUrl;
      }

      await supabase.from("players").update(updates).eq("id", editingPlayer.id);
    } else {
      const { data: newPlayer } = await supabase
        .from("players")
        .insert({
          name: form.name,
          jersey_number: Number(form.jersey_number),
          goal_amount: Number(form.goal_amount),
          parent_email: form.parent_email || null,
          is_active: true,
        })
        .select()
        .single();

      if (newPlayer && formPhoto) {
        const photoUrl = await uploadPhoto(newPlayer.id, formPhoto);
        if (photoUrl) {
          await supabase
            .from("players")
            .update({ photo_url: photoUrl })
            .eq("id", newPlayer.id);
        }
      }
    }

    setSaving(false);
    setShowForm(false);
    setFormPhoto(null);
    setFormPhotoPreview(null);
    fetchPlayers();
  };

  const toggleActive = async (player) => {
    await supabase
      .from("players")
      .update({ is_active: !player.is_active })
      .eq("id", player.id);
    fetchPlayers();
  };

  const handlePhotoUpload = async (player, file) => {
    if (!file) return;
    setUploadingPhoto(player.id);

    const photoUrl = await uploadPhoto(player.id, file);
    if (!photoUrl) {
      console.error("Photo upload failed for player:", player.id);
    }

    setUploadingPhoto(null);
    fetchPlayers();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading players...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-navy">Players</h1>
        <button
          onClick={openAdd}
          className="bg-navy text-white text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-navy-light transition-colors"
        >
          + Add Player
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
          <h2 className="font-semibold text-navy mb-4">
            {editingPlayer ? `Edit ${editingPlayer.name}` : "Add New Player"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">
                  Jersey #
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={form.jersey_number}
                  onChange={(e) =>
                    setForm({ ...form, jersey_number: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">
                  Goal ($)
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={form.goal_amount}
                  onChange={(e) =>
                    setForm({ ...form, goal_amount: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">
                Parent Email <span className="font-normal text-navy/40">(for donation alerts)</span>
              </label>
              <input
                type="email"
                value={form.parent_email}
                onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
                placeholder="parent@email.com"
                className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">
                Player Photo
              </label>
              <div className="flex items-center gap-4">
                {formPhotoPreview && (
                  <img
                    src={formPhotoPreview}
                    alt="Preview"
                    className="w-16 h-16 rounded-full object-cover border-2 border-warm-gray"
                  />
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 border border-warm-gray rounded-lg text-sm text-navy/60 hover:bg-cream/50 transition-colors">
                  <span>{formPhotoPreview ? "Change Photo" : "Choose Photo"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormPhoto(file);
                        setFormPhotoPreview(URL.createObjectURL(file));
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
                {formPhotoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormPhoto(null);
                      setFormPhotoPreview(null);
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingPlayer ? "Update Player" : "Add Player"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border border-warm-gray text-navy/60 text-sm font-medium py-2.5 px-6 rounded-lg hover:bg-warm-gray transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Players list */}
      <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-gray bg-cream/50">
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  Player
                </th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  #
                </th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">
                  Goal
                </th>
                <th className="text-center px-5 py-2.5 font-semibold text-navy/60">
                  Photo
                </th>
                <th className="text-center px-5 py-2.5 font-semibold text-navy/60">
                  Status
                </th>
                <th className="text-right px-5 py-2.5 font-semibold text-navy/60">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-warm-gray last:border-0 hover:bg-cream/30"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {p.photo_url ? (
                        <img
                          src={p.photo_url}
                          alt={p.name}
                          className="w-8 h-8 rounded-full object-cover border border-warm-gray"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-warm-gray flex items-center justify-center">
                          <span className="text-navy/40 text-xs font-bold">
                            {p.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <span className="font-medium text-navy">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-navy/50">{p.jersey_number}</td>
                  <td className="px-5 py-3 text-right text-navy/60">
                    ${p.goal_amount}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <label className="cursor-pointer">
                      <span
                        className={`text-xs font-medium ${
                          uploadingPhoto === p.id
                            ? "text-navy/40"
                            : p.photo_url
                            ? "text-green-600 hover:text-green-700"
                            : "text-red-500 hover:text-red-700"
                        }`}
                      >
                        {uploadingPhoto === p.id
                          ? "Uploading..."
                          : p.photo_url
                          ? "Replace"
                          : "Upload"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingPhoto === p.id}
                        onChange={(e) => {
                          if (e.target.files[0]) handlePhotoUpload(p, e.target.files[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-warm-gray text-navy/40"
                      }`}
                    >
                      {p.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => openEdit(p)}
                      className="text-navy/50 hover:text-navy text-xs font-medium mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(p)}
                      className={`text-xs font-medium ${
                        p.is_active
                          ? "text-red-500 hover:text-red-700"
                          : "text-green-600 hover:text-green-700"
                      }`}
                    >
                      {p.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
              {players.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-navy/40">
                    No players yet. Click "Add Player" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
